const CATEGORY_PARAM = 'lm_category_id';
const PACKAGE_PARAM = 'lm_package_size';
const STORAGE_KEY = 'lm_public_launch_intent';
const TTL = 24 * 60 * 60 * 1000;
const PACKAGE_SIZES = new Set([30, 50, 100, 200, 500]);

export interface PublicLaunchIntent {
    categoryId: number;
    packageSize: number;
    capturedAt: string;
}

function normalizeIntent(
    categoryValue: string | null,
    packageValue: string | null,
    capturedAtValue?: string | null
): PublicLaunchIntent | null {
    const categoryId = Number(categoryValue);
    const packageSize = Number(packageValue);
    if (!Number.isInteger(categoryId) || categoryId <= 0 || categoryId > 100000) return null;
    if (!Number.isInteger(packageSize) || !PACKAGE_SIZES.has(packageSize)) return null;
    const capturedAt =
        capturedAtValue && !Number.isNaN(Date.parse(capturedAtValue)) ? capturedAtValue : new Date().toISOString();
    return { categoryId, packageSize, capturedAt };
}

function cleanLaunchParams() {
    try {
        const url = new URL(window.location.href);
        const hasParams = url.searchParams.has(CATEGORY_PARAM) || url.searchParams.has(PACKAGE_PARAM);
        if (!hasParams) return;
        url.searchParams.delete(CATEGORY_PARAM);
        url.searchParams.delete(PACKAGE_PARAM);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
        // Registration remains usable when the History API is unavailable.
    }
}

export function readPublicLaunchIntent(): PublicLaunchIntent | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const timestamp = Date.parse(parsed?.capturedAt);
        if (!Number.isFinite(timestamp) || timestamp > Date.now() || Date.now() - timestamp >= TTL) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return normalizeIntent(
            String(parsed?.categoryId || ''),
            String(parsed?.packageSize || ''),
            typeof parsed?.capturedAt === 'string' ? parsed.capturedAt : null
        );
    } catch {
        return null;
    }
}

export function capturePublicLaunchIntent(): PublicLaunchIntent | null {
    try {
        const params = new URLSearchParams(window.location.search);
        const incoming = normalizeIntent(params.get(CATEGORY_PARAM), params.get(PACKAGE_PARAM));
        cleanLaunchParams();
        if (!incoming) return readPublicLaunchIntent();
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(incoming));
        return incoming;
    } catch {
        return null;
    }
}

export function clearPublicLaunchIntent(expected?: Pick<PublicLaunchIntent, 'categoryId' | 'packageSize'>): boolean {
    try {
        if (expected) {
            const current = readPublicLaunchIntent();
            if (
                !current ||
                current.categoryId !== expected.categoryId ||
                current.packageSize !== expected.packageSize
            ) {
                return false;
            }
        }
        window.localStorage.removeItem(STORAGE_KEY);
        return true;
    } catch {
        // The lead stream remains usable if storage is unavailable.
        return false;
    }
}
